// Slice 2N · Profitability workspace summary strip (PASS 2).
//
// Read-only card mounted inside the Project Workspace. Loads the
// deterministic `/profitability/summary` snapshot from the PASS 1
// backend and lets the contractor open the full Profitability
// workspace. Never mutates any data. Fails safe — a network error or
// missing snapshot leaves the "not started" copy in place.
//
// "Levi recommends. The contractor decides." — this card never
// reasons about the numbers on its own; it only surfaces backend
// values verbatim and hands off to the workspace for interaction.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { LineChart, ChevronRight } from "lucide-react";

const money = (v, lang) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(lang === "es" ? "es-US" : "en-US", {
      style: "currency", currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch (_) {
    return `$${Math.round(n)}`;
  }
};

const ProfitabilityCard = ({ projectId }) => {
  const { t, lang } = useLang();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/projects/${projectId}/profitability/summary`)
      .then((r) => { if (!cancelled) setSnap(r.data || null); })
      .catch(() => { if (!cancelled) setSnap(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  const state = snap?.state || "not_started";
  const revised = snap?.revenue?.revised_contract_value ?? 0;
  const expectedProfit = snap?.expected?.expected_profit_amount ?? 0;
  const actualProfit = snap?.profit?.actual_profit_amount ?? 0;
  // D-6 / D-1 correction: mirror the backend support flags exactly.
  // Never present a value as authoritative when the backend marks it
  // unsupported — including on this compact card.
  const actualSupported = !!snap?.profit?.supported;
  const expectedSupported = !!snap?.expected?.supported;
  const revisedSupported = !!snap?.revenue?.original_supported;

  return (
    <section data-testid="profitability-card" className="jc-card p-5 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <LineChart size={16} className="text-[var(--jc-text-soft)] shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="overline">{t("profitability.card_overline")}</div>
            <div className="text-sm font-semibold" data-testid="profitability-state">
              {t(`profitability.state.${state}`)}
            </div>
            <div className="text-xs text-[var(--jc-text-soft)] mt-0.5">
              {loading ? t("profitability.loading")
                : t(`profitability.state_desc.${state}`)}
            </div>

            {!loading && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div data-testid="profitability-strip-revenue">
                  <div className="text-[var(--jc-text-soft)] uppercase tracking-wider">
                    {t("profitability.strip_revenue")}
                  </div>
                  <div className="text-sm font-semibold">
                    {revisedSupported ? money(revised, lang)
                      : t("profitability.value_unavailable")}
                  </div>
                </div>
                <div data-testid="profitability-strip-expected">
                  <div className="text-[var(--jc-text-soft)] uppercase tracking-wider">
                    {t("profitability.strip_expected_profit")}
                  </div>
                  <div className="text-sm font-semibold">
                    {expectedSupported ? money(expectedProfit, lang)
                      : t("profitability.value_unavailable")}
                  </div>
                </div>
                <div data-testid="profitability-strip-actual">
                  <div className="text-[var(--jc-text-soft)] uppercase tracking-wider">
                    {t("profitability.strip_actual_profit")}
                  </div>
                  <div className="text-sm font-semibold">
                    {actualSupported ? money(actualProfit, lang)
                      : t("profitability.value_unavailable")}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <Link
          to={`/app/projects/${projectId}/profitability`}
          data-testid="profitability-open-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md jc-btn-primary"
        >
          {state === "not_started"
            ? t("profitability.open_start")
            : t("profitability.open_workspace")}
          <ChevronRight size={14} />
        </Link>
      </div>
    </section>
  );
};

export default ProfitabilityCard;
